
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { getClient } from '../../services/clientService';

export default function ClientDeposit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadClient = async () => {
      try {
        const data = await getClient(id);
        if (!data) {
          setError('Client record not found.');
          setLoading(false);
          return;
        }
        if (data.depositPaid) {
          // Already paid — redirect to login
          navigate('/client/login');
          return;
        }
        setClient(data);
      } catch (err) {
        console.error('Error loading client:', err);
        setError('Could not load your information.');
      } finally {
        setLoading(false);
      }
    };

    if (id) loadClient();
  }, [id, navigate]);

  const handlePayDeposit = async () => {
    setProcessing(true);
    setError('');

    try {
      const response = await fetch(
        'https://us-central1-the-apn.cloudfunctions.net/createClientDepositSession',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            clientId: client.id,
            email: client.contactEmail,
            companyName: client.companyName,
            successUrl: `${window.location.origin}/client/deposit/${client.id}/success`,
            cancelUrl: `${window.location.origin}/client/deposit/${client.id}`,
          }),
        }
      );

      const data = await response.json();

      if (data.url) {
        window.location.href = data.url;
      } else {
        setError('Could not create checkout session. Please try again.');
      }
    } catch (err) {
      console.error('Deposit error:', err);
      setError('An error occurred processing your deposit. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-apn-blue"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 px-4">
      <div className="sm:mx-auto sm:w-full sm:max-w-lg">
        <Link to="/" className="flex justify-center mb-6">
          <img
            src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-blue.png`}
            alt="APN Logo"
            className="h-16"
          />
        </Link>

        <div className="bg-white shadow-lg rounded-lg overflow-hidden">
          <div className="bg-apn-blue text-white p-6 text-center">
            <h2 className="text-2xl font-bold">Activate Your Site Preview</h2>
            <p className="text-blue-100 mt-1">Complete a $5 deposit to access your preview</p>
          </div>

          <div className="p-8">
            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {client ? (
              <>
                <div className="mb-6 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Company</span>
                    <span className="font-medium text-gray-900">{client.companyName}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Contact</span>
                    <span className="font-medium text-gray-900">{client.contactName}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Email</span>
                    <span className="font-medium text-gray-900">{client.contactEmail}</span>
                  </div>
                  <hr className="border-gray-200" />
                  <div className="flex justify-between">
                    <span className="text-gray-900 font-semibold">Preview Deposit</span>
                    <span className="text-2xl font-bold text-apn-blue">$5.00</span>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <h4 className="text-sm font-semibold text-blue-800 mb-2">What you get:</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li className="flex items-center">
                      <svg className="h-4 w-4 text-blue-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      48-hour access to your live site preview
                    </li>
                    <li className="flex items-center">
                      <svg className="h-4 w-4 text-blue-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      Real-time project status tracking
                    </li>
                    <li className="flex items-center">
                      <svg className="h-4 w-4 text-blue-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      Deposit applied toward final project cost
                    </li>
                  </ul>
                </div>

                <button
                  onClick={handlePayDeposit}
                  disabled={processing}
                  className="w-full py-3 px-4 bg-apn-blue text-white font-semibold rounded-lg hover:bg-apn-dark-blue transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {processing ? (
                    <>
                      <svg className="animate-spin h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Processing...
                    </>
                  ) : (
                    'Pay $5 Deposit & Activate Preview'
                  )}
                </button>

                <p className="mt-4 text-xs text-gray-500 text-center">
                  Secure payment processed by Stripe. Your deposit will be applied to your final project cost.
                </p>
              </>
            ) : (
              <p className="text-center text-gray-600">
                Client record not found. Please contact our team.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
